import type Keycloak from 'keycloak-js';
import { fetchFloristInfo, getUserById } from '~/composables/fetchFloristInfo';
import { getGroup, getGroupMembers, getUser } from '~/services/keycloak';
import { getMultiShop } from '~/services/multi-shop';
import { RouteNames } from '~/app/routes/routeNames';
import useOnboarding from '~/composables/onboarding/useOnboarding';
import { UserType } from '~/composables/onboarding/types';

/**
 * Global middleware to load user in store or redirect to the internal part. Runs second (02 prefix).
 * Skipped for /error and /health to avoid redirect loops and blocking health checks.
 */
export default defineNuxtRouteMiddleware(async (to) => {
  if (to.path === '/error' || to.path === '/health' || to.path === '/qrCodeValidation') return;
  if (import.meta.server) return;

  const nuxtApp = useNuxtApp();
  const keycloak = nuxtApp.$keycloak as Keycloak;
  const floristStore = useFloristInfoStore();
  const _isInternalUser = await isInternalUser();
  const _isDeliveryMan = await isDeliveryMan();
  const selectedFlorist = localStorage.getItem('selectedFlorist');

  const { ONBOARDING_GUIDES } = await useCountryParams();
  const onboarding = useOnboarding();

  if (ONBOARDING_GUIDES) {
    onboarding.init({
      userType: _isInternalUser ? UserType.Internal : UserType.Florist,
      userId: keycloak?.subject ?? '',
    });
  }

  if (_isInternalUser) {
    floristStore.setIsInternalUser(true);
    floristStore.setAdminLogin(keycloak?.tokenParsed?.preferred_username);

    if (selectedFlorist) {
      if (floristStore.floristInfo == null || !floristStore.floristInfo.floristId) {
        floristStore.floristInfo = await fetchFloristInfo(selectedFlorist);
        const multiShop = await getMultiShop(floristStore.floristInfo.floristId);
        if (multiShop.length > 1) {
          localStorage.setItem('multiShop', JSON.stringify(multiShop));
        } else if (localStorage.getItem('multiShop')) {
          localStorage.removeItem('multiShop');
        }
      }
    } else {
      // to avoid infinite redirection
      if (to.name !== RouteNames.InternalUserSelect) {
        return navigateTo({ name: RouteNames.InternalUserSelect });
      }
    }
  } else if (_isDeliveryMan) {
    floristStore.setIsDeliveryMan(true);
    floristStore.setDeliveryManId(keycloak?.tokenParsed?.preferred_username);
    floristStore.setDeliveryManInfo(await getUserById(floristStore.deliveryManId));
    // to avoid infinite redirection
    if (to.name !== RouteNames.Deliveries && to.name !== RouteNames.OrderView) {
      return navigateTo({ name: RouteNames.Deliveries });
    }
  } else {
    floristStore.setIsInternalUser(false);
    const floristIdFromLogin = await getFloristIdFromLogin(keycloak);
    const floristIdSelected = selectedFlorist || floristIdFromLogin;

    if (floristStore.floristInfo == null || !floristStore.floristInfo.floristId) {
      try {
        floristStore.floristInfo = await fetchFloristInfo(floristIdSelected);
        const multiShop = await getMultiShop(floristStore.floristInfo.floristId);
        if (multiShop.length > 1) {
          localStorage.setItem('multiShop', JSON.stringify(multiShop));
        }
      } catch {
        return navigateTo('/error?errorMessage=floristUnknown', { redirectCode: 301 });
      }
    }
  }

  if (floristStore.floristInfo == null) {
    return navigateTo('/error?errorMessage=floristUnknown', { redirectCode: 301 });
  }
});

const getUnitCountryCodeFromLoginRegEx = /^[a-zA-Z]{2}/;

async function getFloristIdFromLogin(keycloak: Keycloak): Promise<string> {
  let userName = keycloak.tokenParsed?.preferred_username;
  if (userName) {
    if (userName.includes('@')) {
      userName = userName.split('@')[0];
    }
    if (getUnitCountryCodeFromLoginRegEx.test(userName)) {
      userName = userName.substring(2);
    }
    return userName;
  } else {
    return '';
  }
}

enum KeycloakUserRole {
  PORTAL = 'portal_users',
  DELIVERY_MAN = 'delivery',
  // CAPPING = 'capping_mngt',
  // COLLECTION = 'collection_mngt',
  // COMMUNICATION = 'communication_mngt',
}

const isUserInGroup = async (groupKey: KeycloakUserRole): Promise<boolean> => {
  const { keycloak } = useKeycloak();
  const userName = keycloak?.tokenParsed?.preferred_username;

  if (!userName) return false;

  const user = await getUser(userName); //TODO-XL Type getUser
  if (!user.length || !user[0].id) return false;

  const userId = user[0].id;
  const group = await getGroup(groupKey); //TODO-XL Type getGroup
  if (!group.length) return false;

  const groupMembers = await getGroupMembers(group[0].id); // TODO-XL type getGroupMembers
  return groupMembers.some((member: any) => member.id === userId);
};

const isInternalUser = async (): Promise<boolean> => isUserInGroup(KeycloakUserRole.PORTAL);
const isDeliveryMan = async (): Promise<boolean> => isUserInGroup(KeycloakUserRole.DELIVERY_MAN);
